<template>
  <div class="contents">
    <div class="layout">
      <div class="page-top">
        <h2>작가/작품 검색</h2>
        <div class="breadcrumbs">
          <span>컨텐츠 관리</span>
          <span class="color-black">작가/작품 검색</span>
        </div>
      </div>

      <div class="white-panel mt30 pt30 pb30">
        <div class="search-bar">
          <div class="mode-toggle">
            <button
              type="button"
              :class="{ active: mode === 'artist' }"
              @click="mode = 'artist'"
            >
              작가
            </button>
            <button
              type="button"
              :class="{ active: mode === 'artwork' }"
              @click="mode = 'artwork'"
            >
              작품
            </button>
          </div>
          <div class="search-input">
            <input
              v-model="query"
              type="text"
              :placeholder="mode === 'artist' ? '느낌/스타일로 작가 검색 (예: 굵은 선의 감성 일러스트)' : '느낌/스타일로 작품 검색 (예: 따뜻한 색감의 몽환적인 일러스트)'"
              @keyup.enter="doSearch"
            />
            <button type="button" class="navy-button" @click="doSearch" :disabled="loading">
              {{ loading ? '검색 중…' : '검색' }}
            </button>
          </div>
        </div>
        <p class="search-hint">
          의미(느낌) 유사도 기반 검색입니다. 정확한 키워드 일치가 아니라 분위기가 비슷한 결과가 나옵니다. (최대 40개)
        </p>
      </div>

      <!-- 결과 -->
      <div v-if="searched" class="white-panel mt20 pt20 pb30">
        <div v-if="loading" class="state-msg">검색 중…</div>
        <div v-else-if="!results.length" class="state-msg">검색 결과가 없습니다.</div>

        <template v-else>
          <p class="result-count">총 {{ results.length }}건</p>

          <!-- 작가 결과 -->
          <div v-if="mode === 'artist'" class="card-grid">
            <div v-for="artist in results" :key="artist.artistId" class="card artist-card">
              <div class="artist-head">
                <img
                  class="avatar"
                  :src="artist.profileImageUrl || defaultAvatar"
                  @error="onImgError"
                  alt=""
                />
                <div class="artist-meta">
                  <a
                    class="artist-name"
                    :class="{ linkable: artist.artistUserAccountId }"
                    @click="openArtist(artist.artistUserAccountId)"
                  >
                    {{ artist.name }}
                    <span v-if="artist.artistUserAccountId" class="ext">↗</span>
                  </a>
                  <p class="bio">{{ artist.bio }}</p>
                </div>
              </div>
              <div v-if="artist.representativeArtworks?.length" class="rep-artworks">
                <div
                  v-for="art in artist.representativeArtworks.slice(0, 4)"
                  :key="art.artworkUuid"
                  class="rep-thumb"
                  :title="art.title"
                  @click="openPreview({ imageUrl: art.imageUrl, title: art.title, subtitle: artist.name })"
                >
                  <img :src="art.imageUrl" @error="onImgError" alt="" />
                  <span class="rep-title">{{ art.title }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 작품 결과 -->
          <div v-else class="card-grid">
            <div v-for="art in results" :key="art.artworkUuid" class="card artwork-card">
              <div
                class="artwork-img"
                @click="openPreview({ imageUrl: art.imageUrl, title: art.title, subtitle: metaLine(art), artistName: art.artistName })"
              >
                <img :src="art.imageUrl || defaultAvatar" @error="onImgError" alt="" />
              </div>
              <div class="artwork-body">
                <p class="artwork-title">{{ art.title }}</p>
                <p v-if="metaLine(art)" class="artwork-meta">{{ metaLine(art) }}</p>
                <a
                  class="artwork-artist"
                  :class="{ linkable: art.artistUserAccountId }"
                  @click="openArtist(art.artistUserAccountId)"
                >
                  <img class="avatar-sm" :src="art.artistImageUrl || defaultAvatar" @error="onImgError" alt="" />
                  <span>{{ art.artistName }}</span>
                  <span v-if="art.artistUserAccountId" class="ext">↗</span>
                </a>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- 미리보기 팝업 -->
    <div v-if="preview" class="preview-backdrop" @click.self="preview = null">
      <div class="preview-modal">
        <button type="button" class="preview-close" @click="preview = null" aria-label="닫기">×</button>
        <img :src="preview.imageUrl" @error="onImgError" alt="" />
        <div class="preview-info">
          <p class="preview-title">{{ preview.title }}</p>
          <p v-if="preview.subtitle" class="preview-sub">{{ preview.subtitle }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { apiClient } from '@/services/auth-header';

// VUE_APP_REDIRECT_URI 는 OAuth 리다이렉트(어드민 도메인)라 폴백에 쓰면 안 됨(#작가링크 버그).
// dev/stg 빌드는 VUE_APP_PLATFORM_URL 을 세팅(dev-fo/stg-fo), 운영은 미세팅 → mimio.life.
const platformUrl = process.env.VUE_APP_PLATFORM_URL || 'https://mimio.life';
const defaultAvatar =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect width="80" height="80" fill="%23eef0f3"/><text x="50%25" y="54%25" font-size="12" fill="%23aab" text-anchor="middle" dominant-baseline="middle">No Image</text></svg>';

const mode = ref('artist');
const query = ref('');
const results = ref([]);
const loading = ref(false);
const searched = ref(false);
const preview = ref(null);

const metaLine = (art) => [art.genre, art.year].filter(Boolean).join(' · ');

const doSearch = async () => {
  const q = query.value.trim();
  if (!q) {
    alert('검색어를 입력해주세요.');
    return;
  }
  loading.value = true;
  searched.value = true;
  try {
    const { data } = await apiClient.post('/content/artist-search', {
      query: q,
      mode: mode.value,
      limit: 40,
    });
    results.value = data?.results || [];
  } catch (error) {
    console.error('검색 실패', error);
    results.value = [];
    alert(error?.response?.data?.message || '검색 중 오류가 발생했습니다.');
  } finally {
    loading.value = false;
  }
};

const openArtist = (userAccountId) => {
  if (!userAccountId) return;
  window.open(`${platformUrl}/artist/${userAccountId}`, '_blank', 'noopener');
};

const openPreview = (p) => {
  preview.value = p;
};

const onImgError = (e) => {
  if (e.target.src !== defaultAvatar) e.target.src = defaultAvatar;
};
</script>

<style scoped>
.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.mode-toggle {
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
}
.mode-toggle button {
  padding: 9px 22px;
  font-size: 14px;
  background: #fff;
  color: #555;
  border: none;
  cursor: pointer;
}
.mode-toggle button.active {
  background: #101733;
  color: #fff;
  font-weight: 600;
}
.search-input {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 320px;
}
.search-input input {
  flex: 1;
  height: 42px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
}
.search-input .navy-button {
  height: 42px;
  padding: 0 22px;
  white-space: nowrap;
}
.search-hint {
  margin-top: 12px;
  font-size: 12px;
  color: #8a8f98;
}
.state-msg {
  text-align: center;
  color: #9ca3af;
  padding: 48px 0;
  font-size: 14px;
}
.result-count {
  font-size: 13px;
  color: #6b7280;
  margin: 0 4px 14px;
}

/* 2컬럼 카드 */
.card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}
.card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
}

/* 작가 카드 */
.artist-head {
  display: flex;
  gap: 14px;
}
.avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
  background: #eef0f3;
}
.artist-meta {
  min-width: 0;
}
.artist-name {
  font-size: 16px;
  font-weight: 700;
  color: #101733;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.artist-name.linkable {
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
}
.ext {
  font-size: 11px;
}
.bio {
  margin-top: 6px;
  font-size: 13px;
  color: #555;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.rep-artworks {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-top: 14px;
}
.rep-thumb {
  cursor: pointer;
  text-align: center;
}
.rep-thumb img {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 6px;
  background: #eef0f3;
}
.rep-title {
  display: block;
  font-size: 11px;
  color: #777;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 작품 카드 */
.artwork-img {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  background: #eef0f3;
}
.artwork-img img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  display: block;
}
.artwork-body {
  padding-top: 12px;
}
.artwork-title {
  font-size: 15px;
  font-weight: 700;
  color: #101733;
}
.artwork-meta {
  font-size: 12px;
  color: #8a8f98;
  margin-top: 4px;
}
.artwork-artist {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 13px;
  color: #555;
}
.artwork-artist.linkable {
  color: #2563eb;
  cursor: pointer;
}
.avatar-sm {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  background: #eef0f3;
}

/* 미리보기 팝업 */
.preview-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.preview-modal {
  position: relative;
  max-width: 80vw;
  max-height: 88vh;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.preview-modal img {
  max-width: 80vw;
  max-height: 72vh;
  object-fit: contain;
  background: #111;
}
.preview-info {
  padding: 14px 18px;
}
.preview-title {
  font-size: 15px;
  font-weight: 700;
  color: #101733;
}
.preview-sub {
  font-size: 13px;
  color: #777;
  margin-top: 4px;
}
.preview-close {
  position: absolute;
  top: 8px;
  right: 10px;
  z-index: 1;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}
</style>
